import {Component, inject, OnDestroy, OnInit} from "@angular/core";
import {MatDialog} from "@angular/material/dialog";
import {Store} from "@ngrx/store";
import {FormBuilder} from "@angular/forms";
import {map, Subscription} from "rxjs";
import {ConfirmationComponent} from "../../../shared/components/confirmation/confirmation.component";
import {selectBaselineFundReport, selectReportDate, selectSelectedFundReport} from "../../store/fund-report/fund-report.selectors";
import {FundReportActions} from "../../store/fund.actions";
import {numberNotBiggerThan190000m} from "../../../shared/utils/form-validators";
import {DecimalFormatPipe} from "../../../shared/pipes/decimal-format/decimal-format.pipe";
import {CalculationUtil} from "../../../shared/utils/calculation-util";
import Big from "big.js";
import {EMPTY_FUND_REPORT, FundReport} from "../../models/fund-report";
import {selectIsAnyFundFormInEditMode, selectSelectedFund} from "../../store/fund/fund.selectors";
import {TraceableMoney} from "../../../shared/model/traceable";
import {selectIsTopDownValuation} from "../../store/fund-valuation/fund-valuation.selectors";


@Component({
    selector: "valumize-fund-report-details",
    templateUrl: "./fund-report-details.component.html",
    styleUrls: ["./fund-report-details.component.scss"],
    standalone: false
})
export class FundReportDetailsComponent implements OnInit, OnDestroy {
    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);
    private readonly dialog = inject(MatDialog);


    fundReport$ = this.store.select(selectSelectedFundReport);
    reportDate$ = this.store.select(selectReportDate);
    isTopDown$ = this.store.select(selectIsTopDownValuation);
    isEditDisabled$ = this.store.select(selectIsAnyFundFormInEditMode);

    fundReport = EMPTY_FUND_REPORT;
    fundId?: number;
    fundSize: TraceableMoney | undefined;
    isDataset = false;
    baselineReport: FundReport = EMPTY_FUND_REPORT;

    private readonly calculated = "CALCULATED";
    private readonly reported = "REPORTED";
    private isTopDown = false;

    subscriptions: Subscription[] = [];
    calculationSubscriptions: Subscription[] = [];

    fundReportDetailFormGroup = this.formBuilder.group({
        netCurrentAssets: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
        gpCarryReserve: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
        totalReportedNav: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
        totalDrawDowns: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
        totalDistributions: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
        recallableDistributions: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
        carryPaidToDate: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
        tvpiNavType: this.formBuilder.control<string>(this.calculated)
    });

    get isReportedNavAvailable(): boolean {
        return this.fundReportDetailFormGroup.controls.totalReportedNav.value != null;
    }

    private get defaultTvpiNavType(): string {
        return this.isTopDown ? this.reported : this.calculated;
    }

    get displayedTVPI() {
        const useReportedNav = this.fundReportDetailFormGroup.controls.tvpiNavType.value === this.reported;
        return useReportedNav ? this.fundReport.totalTvpiReportedNav : this.fundReport.totalTVPI;
    }

    private get isGpCarryReservePositive(): boolean {
        const value = this.fundReportDetailFormGroup.controls.gpCarryReserve.value;
        return value != null && value > 0;
    }

    private get isUndrawnNegative(): boolean {
        // All terms are in raw (full) units: fundSize.amount is raw; transformToMillionsNum
        // converts the millions-denominated form values back to raw. Big.js keeps the
        // subtraction/addition decimal-exact per the project convention.
        const fundSize = CalculationUtil.toBig(this.fundSize?.amount) ?? new Big(0);
        const drawDowns = CalculationUtil.toBig(DecimalFormatPipe.transformToMillionsNum(
            this.fundReportDetailFormGroup.controls.totalDrawDowns.value ?? undefined)) ?? new Big(0);
        const recallable = CalculationUtil.toBig(DecimalFormatPipe.transformToMillionsNum(
            this.fundReportDetailFormGroup.controls.recallableDistributions.value ?? undefined)) ?? new Big(0);
        return fundSize.minus(drawDowns).plus(recallable).lt(0);
    }

    constructor() {
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.isTopDown$.subscribe(isTopDown => this.isTopDown = isTopDown)
        );

        this.subscriptions.push(
            this.store.select(selectSelectedFund).pipe(map((fund) => {
                this.fundId = fund.fund.id;
                this.fundSize = fund.fund.size;
            })).subscribe());

        this.subscriptions.push(this.store.select(selectSelectedFundReport).pipe(
            map(fundReport => {
                this.fundReport = fundReport.fundReport.data;
                this.fundReportDetailFormGroup.patchValue({
                    netCurrentAssets: DecimalFormatPipe.transformFromMillionsNum(fundReport.fundReport.data.netCurrentAssets.amount ?? undefined),
                    gpCarryReserve: DecimalFormatPipe.transformFromMillionsNum(fundReport.fundReport.data.gpCarryReserve.amount ?? undefined),
                    totalReportedNav: DecimalFormatPipe.transformFromMillionsNum(fundReport.fundReport.data.totalReportedNav.amount ?? undefined),
                    totalDrawDowns: DecimalFormatPipe.transformFromMillionsNum(fundReport.fundReport.data.totalDrawDowns.amount ?? undefined),
                    totalDistributions: DecimalFormatPipe.transformFromMillionsNum(fundReport.fundReport.data.totalDistributions.amount ?? undefined),
                    recallableDistributions: DecimalFormatPipe.transformFromMillionsNum(fundReport.fundReport.data.recallableDistributions.amount ?? undefined),
                    carryPaidToDate: DecimalFormatPipe.transformFromMillionsNum(fundReport.fundReport.data.carryPaidToDate.amount ?? undefined),
                    tvpiNavType: fundReport.fundReport.data.tvpiNavType?.code ?? this.defaultTvpiNavType
                });
            })).subscribe());

        this.subscriptions.push(
            this.store.select(selectBaselineFundReport).pipe(map((baselineFundReport) => {
                if (baselineFundReport.data.id) {
                    this.isDataset = true;
                    this.baselineReport = baselineFundReport.data;
                } else {
                    this.isDataset = false;
                }
            })).subscribe());
    }

    edit() {
        this.store.dispatch(FundReportActions.edit());
    }

    save() {
        if (this.isGpCarryReservePositive) {
            const dialogRef = this.dialog.open(ConfirmationComponent);
            dialogRef.componentInstance.confirmMessage = "Save with a positive GP Carry Reserve?";
            dialogRef.componentInstance.htmlContent =
                "A positive GP Carry Reserve indicates a clawback state. Please confirm this is intentional before saving.";
            dialogRef.afterClosed().pipe(map(confirmed => {
                if (confirmed) {
                    this.performSave();
                }
            })).subscribe();
        } else if (this.isUndrawnNegative) {
            const dialogRef = this.dialog.open(ConfirmationComponent);
            dialogRef.componentInstance.confirmMessage = "Save with negative total undrawn?";
            dialogRef.componentInstance.htmlContent =
                "The calculated total undrawn is negative (drawdowns exceed fund size). Please confirm this is intentional before saving.";
            dialogRef.afterClosed().pipe(map(confirmed => {
                if (confirmed) {
                    this.performSave();
                }
            })).subscribe();
        } else {
            this.performSave();
        }
    }

    private performSave() {
        this.calculationSubscriptions.forEach(subscription => subscription.unsubscribe());
        if (this.fundId) {
            this.store.dispatch(FundReportActions.save(
                {
                    fundId: this.fundId,
                    fundReport: {
                        ...this.fundReport,
                        ...{
                            netCurrentAssets: {
                                ...this.fundReport.netCurrentAssets,
                                amount: DecimalFormatPipe.transformToMillionsNum(this.fundReportDetailFormGroup.controls.netCurrentAssets.value ?? undefined)
                            },
                            gpCarryReserve: {
                                ...this.fundReport.gpCarryReserve,
                                amount: DecimalFormatPipe.transformToMillionsNum(this.fundReportDetailFormGroup.controls.gpCarryReserve.value ?? undefined)
                            },
                            totalReportedNav: {
                                ...this.fundReport.totalReportedNav,
                                amount: DecimalFormatPipe.transformToMillionsNum(this.fundReportDetailFormGroup.controls.totalReportedNav.value ?? undefined)
                            },
                            totalDrawDowns: {
                                ...this.fundReport.totalDrawDowns,
                                amount: DecimalFormatPipe.transformToMillionsNum(this.fundReportDetailFormGroup.controls.totalDrawDowns.value ?? undefined)
                            },
                            totalDistributions: {
                                ...this.fundReport.totalDistributions,
                                amount: DecimalFormatPipe.transformToMillionsNum(this.fundReportDetailFormGroup.controls.totalDistributions.value ?? undefined)
                            },
                            recallableDistributions: {
                                ...this.fundReport.recallableDistributions,
                                amount: DecimalFormatPipe.transformToMillionsNum(this.fundReportDetailFormGroup.controls.recallableDistributions.value ?? undefined)
                            },
                            carryPaidToDate: {
                                ...this.fundReport.carryPaidToDate,
                                amount: DecimalFormatPipe.transformToMillionsNum(this.fundReportDetailFormGroup.controls.carryPaidToDate.value ?? undefined)
                            },
                            tvpiNavType: {
                                ...this.fundReport.tvpiNavType,
                                code: this.fundReportDetailFormGroup.controls.tvpiNavType.value ?? this.defaultTvpiNavType
                            }
                        }
                    }
                }
            ));
        }
    }

    cancel() {
        this.calculationSubscriptions.forEach(subscription => subscription.unsubscribe());
        this.store.dispatch(FundReportActions.cancel());
    }

    triggerCalculation() {
        if (this.fundReportDetailFormGroup.valid) {
            const form = this.fundReportDetailFormGroup.getRawValue();
            this.store.dispatch(FundReportActions.calc({
                fundReport: {
                    ...this.fundReport,
                    netCurrentAssets: {
                        ...this.fundReport.netCurrentAssets,
                        amount: DecimalFormatPipe.transformToMillionsNum(form.netCurrentAssets)
                    },
                    gpCarryReserve: {
                        ...this.fundReport.gpCarryReserve,
                        amount: DecimalFormatPipe.transformToMillionsNum(form.gpCarryReserve)
                    },
                    totalReportedNav: {
                        ...this.fundReport.totalReportedNav,
                        amount: DecimalFormatPipe.transformToMillionsNum(form.totalReportedNav)
                    },
                    totalDrawDowns: {
                        ...this.fundReport.totalDrawDowns,
                        amount: DecimalFormatPipe.transformToMillionsNum(form.totalDrawDowns)
                    },
                    totalDistributions: {
                        ...this.fundReport.totalDistributions,
                        amount: DecimalFormatPipe.transformToMillionsNum(form.totalDistributions)
                    },
                    recallableDistributions: {
                        ...this.fundReport.recallableDistributions,
                        amount: DecimalFormatPipe.transformToMillionsNum(form.recallableDistributions)
                    },
                    carryPaidToDate: {
                        ...this.fundReport.carryPaidToDate,
                        amount: DecimalFormatPipe.transformToMillionsNum(form.carryPaidToDate)
                    },
                    tvpiNavType: {
                        ...this.fundReport.tvpiNavType,
                        code: form.tvpiNavType ?? this.defaultTvpiNavType
                    }
                }
            }));
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.calculationSubscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
